import { X, Mail, User, Calendar, Phone, MapPin, Briefcase, Users, Home, FileText, ChevronDown, ShieldCheck } from 'lucide-react';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addPatient } from '../../redux/patientSlice';
import { checkEmailExists, checkMobileExists, sendOtp, verifyOtp } from '../../redux/authSlice';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import { docPatCreationCountryList } from '../../redux/masterSlice';
import CountryTimeZoneSelect from '../common/CountryTimeZoneSelect';
import GenderSelect from '../common/GenderSelect';
import PhoneCountryDropdown from '../common/PhoneCountryDropdown';
import { getGenderOptions, formatFirstName, formatSurname } from '../../utils/personFields';



export default function AddPatient({ isOpen, onClose, onSubmit }) {
    const dispatch = useDispatch();
    const { t } = useTranslation(['addPatient']);
    const userRole = localStorage.getItem('Role');
    const { isLoading: patientLoading, error: patientError } = useSelector((state) => state.patient);
    const { emailCheckLoading, mobileCheckLoading, sendOtpLoading, verifyOtpLoading } = useSelector((state) => state.auth);
    const { countryTimezoneList, countries } = useSelector((state) => state.master);
    const axiosInstance = useAxiosAuth();
    const [emailStatus, setEmailStatus] = useState(null); // null: not checked, true: available, false: exists
    const emailCheckTimeoutRef = useRef(null);
    const lastCheckedEmailRef = useRef(''); // Track last checked email
    const isEmailCheckInProgressRef = useRef(false); // Track if email check is in progress
    const [phoneStatus, setPhoneStatus] = useState(null); // null: not checked, true: available, false: exists
    const phoneCheckTimeoutRef = useRef(null);
    const lastCheckedPhoneRef = useRef(''); // Track last checked phone number
    const isPhoneCheckInProgressRef = useRef(false); // Track if phone check is in progress

    // --- Email OTP state ---
    const [emailOtpSent, setEmailOtpSent] = useState(false);
    const [emailOtpCode, setEmailOtpCode] = useState('');
    const [emailOtpVerified, setEmailOtpVerified] = useState(false);
    const [emailOtpError, setEmailOtpError] = useState('');
    const [emailOtpTimer, setEmailOtpTimer] = useState(0);
    const emailOtpTimerRef = useRef(null);
    const emailOtpVerifyingRef = useRef(false);

    // --- Phone OTP state ---
    const [phoneOtpSent, setPhoneOtpSent] = useState(false);
    const [phoneOtpCode, setPhoneOtpCode] = useState('');
    const [phoneOtpVerified, setPhoneOtpVerified] = useState(false);
    const [phoneOtpError, setPhoneOtpError] = useState('');
    const [phoneOtpTimer, setPhoneOtpTimer] = useState(0);
    const phoneOtpTimerRef = useRef(null);
    const phoneOtpVerifyingRef = useRef(false);

    // Store dispatch and axiosInstance in refs to avoid dependency issues
    const dispatchRef = useRef(dispatch);
    const axiosInstanceRef = useRef(axiosInstance);

    // Update refs when values change
    useEffect(() => {
        dispatchRef.current = dispatch;
        axiosInstanceRef.current = axiosInstance;
    }, [dispatch, axiosInstance]);

    useEffect(() => {
        dispatch(docPatCreationCountryList());
    }, [dispatch]);

    // Proper email regex: allows letters, numbers, dots, hyphens, underscores, and plus signs in local part
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        gender: '',
        dob: '',
        occupation: '',
        email: '',
        phone: '',
        altPhone: '',
        address: '',
        additional: '',
        countryId: '',
        timeZone: '',
        city: '',
        zipCode: '',
        age: '',
    });
    const [errors, setErrors] = useState({});
    const selectedCountry = countryTimezoneList?.find(c => c.countryId === formData.countryId) || null;

    const [apiError, setApiError] = useState(null);
    const [charLimitReached, setCharLimitReached] = useState({});

    // Debounced email check
    useEffect(() => {
        if (!isOpen) return; // Don't check if modal is closed

        // Clear previous timeout
        if (emailCheckTimeoutRef.current) {
            clearTimeout(emailCheckTimeoutRef.current);
        }

        const email = formData.email?.trim() || '';

        // If email is empty or invalid format, reset status
        if (!email || !emailRegex.test(email)) {
            setEmailStatus(null);
            lastCheckedEmailRef.current = '';
            return;
        }

        // If email hasn't changed or check is in progress, don't check again
        if (lastCheckedEmailRef.current === email || isEmailCheckInProgressRef.current) {
            return;
        }

        // Set new timeout for debounced check (500ms delay)
        emailCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedEmail = formData.email.trim();

            // Double check email is still valid and hasn't changed during debounce
            if (!trimmedEmail || !emailRegex.test(trimmedEmail) || lastCheckedEmailRef.current === trimmedEmail || isEmailCheckInProgressRef.current) {
                return;
            }

            // Mark check as in progress and update last checked email BEFORE making the API call
            isEmailCheckInProgressRef.current = true;
            lastCheckedEmailRef.current = trimmedEmail;

            try {
                const resultAction = await dispatchRef.current(checkEmailExists({
                    email: trimmedEmail,
                    axiosInstance: axiosInstanceRef.current
                }));

                // Only update status if the email hasn't changed during the API call
                if (formData.email.trim() === trimmedEmail) {
                    if (checkEmailExists.fulfilled.match(resultAction)) {
                        // exists: true means email already exists (not available)
                        // exists: false means email doesn't exist (available)
                        setEmailStatus(!resultAction.payload.exists);
                    } else {
                        setEmailStatus(null);
                        lastCheckedEmailRef.current = ''; // Reset on error
                    }
                }
            } catch (error) {
                console.error('Error checking email:', error);
                if (formData.email.trim() === trimmedEmail) {
                    setEmailStatus(null);
                    lastCheckedEmailRef.current = ''; // Reset on error
                }
            } finally {
                isEmailCheckInProgressRef.current = false;
            }
        }, 500);

        // Cleanup function
        return () => {
            if (emailCheckTimeoutRef.current) {
                clearTimeout(emailCheckTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.email, isOpen]);

    // Debounced phone check (only for main phone, not altPhone)
    useEffect(() => {
        if (!isOpen) return;

        if (phoneCheckTimeoutRef.current) {
            clearTimeout(phoneCheckTimeoutRef.current);
        }

        const phone = formData.phone?.trim() || '';

        const minLen = selectedCountry?.mobileMinLength || 10;
        const maxLen = selectedCountry?.mobileMaxLength || 10;
        const isValidPhone = selectedCountry && new RegExp(`^\\d{${minLen},${maxLen}}$`).test(phone);

        if (!phone || !isValidPhone) {
            setPhoneStatus(null);
            lastCheckedPhoneRef.current = '';
            return;
        }

        if (lastCheckedPhoneRef.current === phone || isPhoneCheckInProgressRef.current) {
            return;
        }

        phoneCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedPhone = formData.phone.trim();

            const isStillValid = selectedCountry && new RegExp(`^\\d{${selectedCountry.mobileMinLength || 10},${selectedCountry.mobileMaxLength || 10}}$`).test(trimmedPhone);

            if (!trimmedPhone || !isStillValid || lastCheckedPhoneRef.current === trimmedPhone || isPhoneCheckInProgressRef.current) {
                return;
            }

            isPhoneCheckInProgressRef.current = true;
            lastCheckedPhoneRef.current = trimmedPhone;

            try {
                const resultAction = await dispatchRef.current(checkMobileExists({
                    phone: trimmedPhone,
                    axiosInstance: axiosInstanceRef.current
                }));

                if (formData.phone.trim() === trimmedPhone) {
                    if (checkMobileExists.fulfilled.match(resultAction)) {
                        setPhoneStatus(!resultAction.payload.exists);
                    } else {
                        setPhoneStatus(null);
                        lastCheckedPhoneRef.current = '';
                    }
                }
            } catch (error) {
                console.error('Error checking phone:', error);
                if (formData.phone.trim() === trimmedPhone) {
                    setPhoneStatus(null);
                    lastCheckedPhoneRef.current = '';
                }
            } finally {
                isPhoneCheckInProgressRef.current = false;
            }
        }, 500);

        return () => {
            if (phoneCheckTimeoutRef.current) {
                clearTimeout(phoneCheckTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.phone, formData.countryId, isOpen, selectedCountry]);

    const validateForm = () => {
        const newErrors = {};
        const nameRegex = /^[a-zA-ZÀ-ÿ\s\-']+$/; // Letters (incl. accented), spaces, hyphens, and apostrophes
        const addressRegex = /^[A-Za-zÀ-ÿ0-9,\.\-\s]+$/; // Letters (incl. accented), numbers, commas, dots, hyphens
        const safeCharsRegex = /^[A-Za-zÀ-ÿ0-9,\.\-\s]*$/; // Safe characters for additional info
        const alphaSpaceRegex = /^[A-Za-zÀ-ÿ\s]+$/; // Letters (incl. accented) and spaces

        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = t('addPatient.name_is_required');
        } else if (/^\s+$/.test(formData.name)) {
            newErrors.name = t('addPatient.name_is_required');
        } else if (!nameRegex.test(formData.name)) {
            newErrors.name = t('addPatient.invalid_name_format');
        } else if (formData.name.length < 2 || formData.name.length > 20) {
            newErrors.name = t('addPatient.name_length');
        }

        // Surname validation
        if (!formData.surname.trim()) {
            newErrors.surname = t('addPatient.surname_is_required');
        } else if (/^\s+$/.test(formData.surname)) {
            newErrors.surname = t('addPatient.surname_is_required');
        } else if (!nameRegex.test(formData.surname)) {
            newErrors.surname = t('addPatient.invalid_surname_format');
        } else if (formData.surname.length < 2 || formData.surname.length > 20) {
            newErrors.surname = t('addPatient.surname_length');
        }

        // Gender validation
        if (!formData.gender) {
            newErrors.gender = t('addPatient.gender_is_required');
        }

        // DOB validation
        if (!formData.dob) {
            newErrors.dob = t('addPatient.dob_is_required');
        } else if (new Date(formData.dob) > new Date()) {
            newErrors.dob = t('addPatient.dob_cannot_be_future');
        } else if (new Date(formData.dob).getFullYear() < 1900) {
            newErrors.dob = t('addPatient.dob_must_be_after_1900');
        }

        // Occupation validation (optional)
        if (formData.occupation.trim()) {
            if (!alphaSpaceRegex.test(formData.occupation)) {
                newErrors.occupation = t('addPatient.invalid_occupation_format');
            } else if (formData.occupation.length < 5 || formData.occupation.length > 25) {
                newErrors.occupation = t('addPatient.occupation_length');
            }
        }

        // Email validation
        if (!formData.email.trim()) {
            newErrors.email = t('addPatient.email_is_required');
        } else {
            // Sanitize email - remove dangerous characters
            const sanitizedEmail = formData.email.replace(/[<>]/g, '');
            const email = sanitizedEmail.trim();

            // Check for whitespace-only
            if (/^\s+$/.test(formData.email)) {
                newErrors.email = t('addPatient.email_is_required');
            }
            // Check total length (RFC 5321: max 254 characters)
            else if (email.length > 254) {
                newErrors.email = t('addPatient.email_too_long');
            } else {
                // Split email into local and domain parts
                const parts = email.split('@');
                if (parts.length !== 2) {
                    newErrors.email = t('addPatient.invalid_email_format');
                } else {
                    const [localPart, domainPart] = parts;
                    // Local part (before @) should be max 64 characters (RFC 5321)
                    if (localPart.length > 64) {
                        newErrors.email = t('addPatient.email_local_part_too_long');
                    } else if (localPart.length === 0) {
                        newErrors.email = t('addPatient.invalid_email_format');
                    } else if (domainPart.length === 0 || !domainPart.includes('.')) {
                        newErrors.email = t('addPatient.invalid_email_format');
                    } else if (domainPart.length > 253) {
                        newErrors.email = t('addPatient.email_domain_part_too_long');
                    } else if (!emailRegex.test(email)) {
                        newErrors.email = t('addPatient.invalid_email_format');
                    } else if (emailStatus === false) {
                        newErrors.email = t('addPatient.email_already_exists');
                    } else if (emailStatus === null && !emailCheckLoading) {
                        newErrors.email = t('addPatient.verify_email_address');
                    } else if (emailStatus === true && !emailOtpVerified) {
                        newErrors.email = t('addPatient.verify_email_otp');
                    }
                }
            }
        }

        // Phone validation
        if (!formData.phone.trim()) {
            newErrors.phone = t('addPatient.phone_is_required');
        } else {
            const phoneMin = selectedCountry?.mobileMinLength || 10;
            const phoneMax = selectedCountry?.mobileMaxLength || 10;
            const phoneRegex = new RegExp(`^\\+?\\d{${phoneMin},${phoneMax}}$`);
            if (!phoneRegex.test(formData.phone)) {
                newErrors.phone = t('addPatient.invalid_phone_number', { length: phoneMin === phoneMax ? phoneMin : `${phoneMin}-${phoneMax}` });
            } else if (phoneStatus === false) {
                newErrors.phone = t('addPatient.phone_already_exists');
            } else if (phoneStatus === null && !mobileCheckLoading) {
                newErrors.phone = t('addPatient.verify_phone_number');
            } else if (phoneStatus === true && !phoneOtpVerified) {
                newErrors.phone = t('addPatient.verify_phone_otp');
            }
        }

        // Alt Phone validation (optional)
        if (formData.altPhone && formData.altPhone.trim()) {
            const altMin = selectedCountry?.altMinLength || selectedCountry?.mobileMinLength || 10;
            const altMax = selectedCountry?.altMaxLength || selectedCountry?.mobileMaxLength || 10;
            const altPhoneRegex = new RegExp(`^\\+?\\d{${altMin},${altMax}}$`);
            if (!altPhoneRegex.test(formData.altPhone)) {
                newErrors.altPhone = t('addPatient.invalid_alt_phone_number', { length: altMin === altMax ? altMin : `${altMin}-${altMax}` });
            }
        }

        // Address validation
        if (!formData.address.trim()) {
            newErrors.address = t('addPatient.address_is_required');
        } else if (/^\s+$/.test(formData.address)) {
            newErrors.address = t('addPatient.address_is_required');
        } else {
            // Sanitize address - remove dangerous characters
            const sanitizedAddress = formData.address.replace(/[<>]/g, '');
            if (!addressRegex.test(sanitizedAddress)) {
                newErrors.address = t('addPatient.invalid_address_format');
            } else if (sanitizedAddress.length < 5 || sanitizedAddress.length > 100) {
                newErrors.address = t('addPatient.address_length');
            }
        }

        // City validation
        if (!formData.city.trim()) {
            newErrors.city = t('addPatient.city_is_required');
        } else if (/^\s+$/.test(formData.city)) {
            newErrors.city = t('addPatient.city_is_required');
        } else if (!alphaSpaceRegex.test(formData.city)) {
            newErrors.city = t('addPatient.invalid_city_format');
        } else if (formData.city.length < 5 || formData.city.length > 25) {
            newErrors.city = t('addPatient.city_length');
        }

        // Country validation
        if (!formData.countryId) {
            newErrors.countryId = t('addPatient.country_is_required');
        }

        // Zip Code validation
        if (!formData.zipCode.trim()) {
            newErrors.zipCode = t('addPatient.zipcode_is_required');
        } else {
            const zipMin = selectedCountry?.zipMinLength || 5;
            const zipMax = selectedCountry?.zipMaxLength || 6;
            const zipRegex = new RegExp(`^\\d{${zipMin},${zipMax}}$`);
            if (!zipRegex.test(formData.zipCode)) {
                newErrors.zipCode = t('addPatient.invalid_zipcode_format', { length: zipMin === zipMax ? zipMin : `${zipMin}-${zipMax}` });
            }
        }

        // Additional Information validation
        if (formData.additional) {
            // Sanitize additional - remove dangerous characters
            const sanitizedAdditional = formData.additional.replace(/[<>]/g, '');
            if (/^\s+$/.test(formData.additional)) {
                newErrors.additional = t('addPatient.additional_only_whitespace');
            } else if (sanitizedAdditional.length > 150) {
                newErrors.additional = t('addPatient.additional_length');
            } else if (!safeCharsRegex.test(sanitizedAdditional)) {
                newErrors.additional = t('addPatient.invalid_additional_format');
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    function calculateAge(dob) {
        const birthDate = new Date(dob);
        const today = new Date();
        // Use UTC getters for birthDate to avoid timezone shift on DOB
        let age = today.getFullYear() - birthDate.getUTCFullYear();
        const monthDiff = today.getMonth() - birthDate.getUTCMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
            age--;
        }
        return age;
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        // First name: only the first character uppercased.
        if (name === 'name' && processedValue.length > 0) {
            processedValue = formatFirstName(processedValue);
        }
        // Surname (family name): always uppercase.
        if (name === 'surname' && processedValue.length > 0) {
            processedValue = formatSurname(processedValue);
        }

        // Sanitize input - remove dangerous characters for text fields
        if (name === 'email' || name === 'address' || name === 'additional' || name === 'altPhone') {
            processedValue = processedValue.replace(/[<>]/g, '');
        }

        // For zipcode, phone, and altPhone, only allow numbers
        if (name === 'zipCode' || name === 'phone' || name === 'altPhone') {
            processedValue = processedValue.replace(/[^0-9]/g, '');
        }

        // Restrict name and surname to letters, spaces, hyphens, and apostrophes only
        if ((name === 'name' || name === 'surname') && processedValue && !/^[a-zA-ZÀ-ÿ\s\-']*$/.test(processedValue)) {
            return;
        }

        // Restrict email to valid email characters only (letters, numbers, dots, hyphens, underscores, plus, @)
        if (name === 'email' && processedValue) {
            // Allow only valid email characters: letters, numbers, dots, hyphens, underscores, plus, and @
            if (!/^[a-zA-Z0-9._+-@]*$/.test(processedValue)) return;
        }

        // Reset email status and tracking when email changes
        if (name === 'email') {
            setEmailStatus(null);
            lastCheckedEmailRef.current = '';
            isEmailCheckInProgressRef.current = false;
            resetOtpEmailState();
        }

        // Reset phone status and tracking when phone changes
        if (name === 'phone') {
            setPhoneStatus(null);
            lastCheckedPhoneRef.current = '';
            isPhoneCheckInProgressRef.current = false;
            resetOtpPhoneState();
        }

        setFormData((prev) => ({
            ...prev,
            [name]: processedValue,
            ...(name === 'dob' && processedValue ? { age: calculateAge(processedValue) } : {}),
            ...(name === 'country' ? { phone: '', zipCode: '' } : {}),
        }));
        setApiError(null);

        if (name === 'dob') {
            let dobError = null;
            if (!processedValue) {
                dobError = t('addPatient.dob_is_required');
            } else if (new Date(processedValue) > new Date()) {
                dobError = t('addPatient.dob_cannot_be_future');
            } else if (new Date(processedValue).getFullYear() < 1900) {
                dobError = t('addPatient.dob_must_be_after_1900');
            }
            setErrors((prev) => ({ ...prev, dob: dobError }));
        } else {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }

        // Check if character limit is reached
        const maxLengths = {
            name: 20,
            surname: 20,
            zipCode: selectedCountry?.zipMaxLength || 10,
            phone: selectedCountry?.mobileMaxLength || 15
        };

        if (maxLengths[name] && processedValue.length >= maxLengths[name]) {
            setCharLimitReached({ ...charLimitReached, [name]: true });
        } else {
            setCharLimitReached({ ...charLimitReached, [name]: false });
        }
    };

    const resetOtpEmailState = () => {
        setEmailOtpSent(false);
        setEmailOtpCode('');
        setEmailOtpVerified(false);
        setEmailOtpError('');
        setEmailOtpTimer(0);
        if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
        emailOtpVerifyingRef.current = false;
    };

    const resetOtpPhoneState = () => {
        setPhoneOtpSent(false);
        setPhoneOtpCode('');
        setPhoneOtpVerified(false);
        setPhoneOtpError('');
        setPhoneOtpTimer(0);
        if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
        phoneOtpVerifyingRef.current = false;
    };

    const startOtpTimer = (setTimer, timerRef) => {
        if (timerRef.current) clearInterval(timerRef.current);
        setTimer(60);
        timerRef.current = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) { clearInterval(timerRef.current); return 0; }
                return prev - 1;
            });
        }, 1000);
    };

    const handleSendEmailOtp = async () => {
        setEmailOtpError('');
        try {
            const result = await dispatch(sendOtp({ email: formData.email.trim(), axiosInstance })).unwrap();
            if (result?.success) {
                setEmailOtpSent(true);
                setEmailOtpCode('');
                setEmailOtpVerified(false);
                startOtpTimer(setEmailOtpTimer, emailOtpTimerRef);
            }
        } catch (err) {
            setEmailOtpError(typeof err === 'string' ? err : t('addPatient.failed_to_send_otp'));
        }
    };

    const handleVerifyEmailOtp = useCallback(async (code) => {
        const otpVal = code ?? emailOtpCode;
        if (emailOtpVerifyingRef.current || otpVal.length !== 6) return;
        emailOtpVerifyingRef.current = true;
        setEmailOtpError('');
        try {
            const result = await dispatch(verifyOtp({ email: formData.email.trim(), otpCode: otpVal, axiosInstance })).unwrap();
            if (result?.success) {
                setEmailOtpVerified(true);
                if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
                setEmailOtpTimer(0);
            } else {
                setEmailOtpError(t('addPatient.invalid_otp'));
            }
        } catch (err) {
            setEmailOtpError(typeof err === 'string' ? err : t('addPatient.invalid_otp'));
        } finally {
            emailOtpVerifyingRef.current = false;
        }
    }, [emailOtpCode, dispatch, axiosInstance, formData.email]);

    const handleSendPhoneOtp = async () => {
        setPhoneOtpError('');
        try {
            const result = await dispatch(sendOtp({ phone: formData.phone.trim(), axiosInstance })).unwrap();
            if (result?.success) {
                setPhoneOtpSent(true);
                setPhoneOtpCode('');
                setPhoneOtpVerified(false);
                startOtpTimer(setPhoneOtpTimer, phoneOtpTimerRef);
            }
        } catch (err) {
            setPhoneOtpError(typeof err === 'string' ? err : t('addPatient.failed_to_send_otp'));
        }
    };

    const handleVerifyPhoneOtp = useCallback(async (code) => {
        const otpVal = code ?? phoneOtpCode;
        if (phoneOtpVerifyingRef.current || otpVal.length !== 6) return;
        phoneOtpVerifyingRef.current = true;
        setPhoneOtpError('');
        try {
            const result = await dispatch(verifyOtp({ phone: formData.phone.trim(), otpCode: otpVal, axiosInstance })).unwrap();
            if (result?.success) {
                setPhoneOtpVerified(true);
                if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
                setPhoneOtpTimer(0);
            } else {
                setPhoneOtpError(t('addPatient.invalid_otp'));
            }
        } catch (err) {
            setPhoneOtpError(typeof err === 'string' ? err : t('addPatient.invalid_otp'));
        } finally {
            phoneOtpVerifyingRef.current = false;
        }
    }, [phoneOtpCode, dispatch, axiosInstance, formData.phone]);

    // Cleanup OTP timers on unmount
    useEffect(() => {
        return () => {
            if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
            if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
        };
    }, []);

    const resetForm = () => {
        setFormData({
            name: '',
            surname: '',
            gender: '',
            dob: '',
            occupation: '',
            email: '',
            phone: '',
            altPhone: '',
            address: '',
            additional: '',
            countryId: '',
            timeZone: '',
            city: '',
            zipCode: '',
            age: '',
        });
        setErrors({});
        setApiError(null);
        setCharLimitReached({});
        setEmailStatus(null);
        setPhoneStatus(null);
        lastCheckedEmailRef.current = '';
        lastCheckedPhoneRef.current = '';
        isEmailCheckInProgressRef.current = false;
        isPhoneCheckInProgressRef.current = false;
        resetOtpEmailState();
        resetOtpPhoneState();
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        // Ensure email is validated and OTP verified before submission
        if (emailStatus !== true) {
            toast.warn(t('addPatient.verify_email_to_proceed'));
            return;
        }
        if (!emailOtpVerified) {
            toast.warn(t('addPatient.verify_email_otp'));
            return;
        }

        // Ensure phone is validated and OTP verified before submission
        if (phoneStatus !== true) {
            toast.warn(t('addPatient.verify_phone_to_proceed'));
            return;
        }
        if (!phoneOtpVerified) {
            toast.warn(t('addPatient.verify_phone_otp'));
            return;
        }

        const result = await Swal.fire({
            title: t('addPatient.confirm_title'),
            text: t('addPatient.confirm_text'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('addPatient.confirm_button'),
            cancelButtonText: t('addPatient.cancel'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            const payload = {
                name: formData.name,
                surname: formData.surname,
                gender: formData.gender,
                dob: formData.dob,
                occupation: formData.occupation,
                email: formData.email,
                phone: formData.phone,
                altPhone: formData.altPhone,
                address: formData.address,
                additional: formData.additional,
                countryId: formData.countryId,
                timeZone: formData.timeZone,
                city: formData.city,
                zipCode: formData.zipCode,
                profileStatus: 'complete',
                ...(emailOtpVerified && { emailVerified: true }),
                ...(phoneOtpVerified && { phoneNoVerified: true }),
            };

            // Add doctorId to payload if user is a doctor
            if (userRole === 'doctor') {
                const doctorId = localStorage.getItem('ID');
                if (doctorId) {
                    payload.doctorId = doctorId;
                }
            }

            await dispatch(addPatient(payload)).unwrap();

            toast.success(t('addPatient.success_text'));

            resetForm();
            if (typeof onSubmit === 'function') {
                onSubmit({ success: true });
            }
            onClose();
        } catch (err) {
            const errorMessage =
                err?.response?.data?.message?.en ||
                err?.message ||
                t('addPatient.failed_to_add_patient');

            setApiError(errorMessage);

            toast.warn(errorMessage);
        }
    };

    if (!isOpen) return null;

    return (
        <div data-testid="add-patient-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div data-testid="add-patient-modal-content" className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                {/* Header Section - Sticky */}
                <div data-testid="add-patient-modal-header" className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                    <h3 data-testid="add-patient-modal-title" className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">{t('addPatient.heading')}</h3>
                    <button
                        data-testid="close-add-patient-modal"
                        id="btn-close-modal"
                        onClick={handleClose}
                        className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                    >
                        <X data-testid="close-modal-icon" className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                </div>

                {/* Scrollable Form Content */}
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                    {apiError && (
                        <div data-testid="add-patient-api-error" className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-xl mb-4 shadow-md">
                            {apiError}
                        </div>
                    )}
                    <form data-testid="add-patient-form" onSubmit={handleSubmit} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${patientLoading ? "opacity-50 pointer-events-none" : ""}`}>
                        <div data-testid="basic-information-section" className="sm:col-span-2 lg:col-span-3">
                            <h4 data-testid="basic-information-title" className="text-sm font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-4">{t('addPatient.basic_information')}</h4>
                        </div>
                        {/* 1. Gender */}
                        <div data-testid="gender-field-container">
                            <label htmlFor="gender" data-testid="gender-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.gender')} <span className="text-red-500">*</span></label>
                            <GenderSelect
                                value={formData.gender}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, gender: val }));
                                    setErrors(prev => ({ ...prev, gender: null }));
                                }}
                                options={getGenderOptions(t)}
                                placeholder={t('addPatient.select_gender')}
                                error={!!errors.gender}
                            />
                            {errors.gender && <p data-testid="gender-error" className="mt-2 text-xs text-red-500 font-medium">{errors.gender}</p>}
                        </div>
                        {/* 2. First Name */}
                        <div data-testid="name-field-container">
                            <label htmlFor="name" data-testid="name-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.name')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="name-input"
                                    id="input-name"
                                    name="name"
                                    type="text"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.name_placeholder')}
                                    maxLength={20}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.name && <p data-testid="name-error" className="mt-2 text-xs text-red-500 font-medium">{errors.name}</p>}
                            {charLimitReached.name && <p className="mt-2 text-xs text-red-500 font-medium">{t('addPatient.reached_text_limit', { limit: 20 })}</p>}
                        </div>
                        {/* 3. Surname */}
                        <div data-testid="surname-field-container">
                            <label htmlFor="surname" data-testid="surname-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.surname')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.surname ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="surname-input"
                                    id="input-surname"
                                    name="surname"
                                    type="text"
                                    value={formData.surname}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.surname_placeholder')}
                                    maxLength={20}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm uppercase"
                                />
                            </div>
                            {errors.surname && <p data-testid="surname-error" className="mt-2 text-xs text-red-500 font-medium">{errors.surname}</p>}
                            {charLimitReached.surname && <p className="mt-2 text-xs text-red-500 font-medium">{t('addPatient.reached_text_limit', { limit: 20 })}</p>}
                        </div>
                        <div data-testid="dob-field-container">
                            <label htmlFor="dob" data-testid="dob-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.dob')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.dob ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Calendar className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="dob-input"
                                    id="input-dob"
                                    name="dob"
                                    type="date"
                                    value={formData.dob}
                                    onChange={handleChange}
                                    min="1900-01-01"
                                    max={new Date().toISOString().split('T')[0]}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 text-sm"
                                />
                            </div>
                            {errors.dob && <p data-testid="dob-error" className="mt-2 text-xs text-red-500 font-medium">{errors.dob}</p>}
                        </div>
                        <div data-testid="age-field-container">
                            <label htmlFor="age" data-testid="age-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.age')}</label>
                            <div className="relative flex items-center border rounded-xl p-3 bg-gray-100 border-gray-300">
                                <Calendar className="text-gray-400 w-5 h-5 mr-2" />
                                <input
                                    data-testid="age-input"
                                    id="input-age"
                                    name="age"
                                    type="number"
                                    value={formData.dob && !errors.dob ? calculateAge(formData.dob) : ''}
                                    readOnly
                                    className="w-full bg-transparent focus:outline-none text-gray-800 text-sm cursor-not-allowed"
                                />
                            </div>
                        </div>
                        <div data-testid="occupation-field-container">
                            <label htmlFor="occupation" data-testid="occupation-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.occupation')}</label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.occupation ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Briefcase className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="occupation-input"
                                    id="input-occupation"
                                    name="occupation"
                                    type="text"
                                    value={formData.occupation}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.occupation_placeholder')}
                                    maxLength={25}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.occupation && <p data-testid="occupation-error" className="mt-2 text-xs text-red-500 font-medium">{errors.occupation}</p>}
                        </div>
                        <div data-testid="contact-information-section" className="sm:col-span-2 lg:col-span-3">
                            <div data-testid="section-divider" className="border border-gray-200 h-[1px] my-10"></div>
                            <h4 data-testid="contact-information-title" className="text-sm font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-4">{t('addPatient.contact_information')}</h4>
                        </div>
                        <div data-testid="email-field-container">
                            <label htmlFor="email" data-testid="email-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.email')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${emailOtpVerified
                                ? 'border-green-400 focus-within:ring-2 focus-within:ring-green-500'
                                : emailStatus === false
                                    ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                                    : emailStatus === true
                                        ? 'border-teal-300 focus-within:ring-2 focus-within:ring-teal-500'
                                        : errors.email
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Mail className="text-[#4ABDBD] w-5 h-5 mr-2 flex-shrink-0" />
                                <input
                                    data-testid="email-input"
                                    id="input-email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.email_placeholder')}
                                    maxLength={254}
                                    disabled={emailOtpVerified}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                                {emailCheckLoading && (
                                    <span className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2 flex-shrink-0"></span>
                                )}
                                {!emailCheckLoading && emailStatus === false && (
                                    <X
                                        className="text-red-500 w-5 h-5 ml-2 flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, email: '' }));
                                            setEmailStatus(null);
                                            setErrors(prev => ({ ...prev, email: null }));
                                            lastCheckedEmailRef.current = '';
                                            resetOtpEmailState();
                                        }}
                                    />
                                )}
                                {!emailCheckLoading && emailStatus === true && !emailOtpVerified && (
                                    <button
                                        type="button"
                                        data-testid="add-patient-email-otp-send-button"
                                        onClick={handleSendEmailOtp}
                                        disabled={sendOtpLoading || emailOtpSent}
                                        className="ml-2 flex-shrink-0 text-xs px-2 py-1 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                    >
                                        {sendOtpLoading ? '...' : emailOtpSent ? t('addPatient.sent') : t('addPatient.send_otp')}
                                    </button>
                                )}
                                {emailOtpVerified && (
                                    <ShieldCheck className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                                )}
                            </div>
                            {errors.email && <p data-testid="email-error" className="mt-2 text-xs text-red-500 font-medium">{errors.email}</p>}
                            {!emailCheckLoading && emailStatus === false && (
                                <p className="text-red-500 text-xs mt-1">{t('addPatient.email_already_exists')}</p>
                            )}
                            {/* Email OTP input row */}
                            {emailOtpSent && !emailOtpVerified && (
                                <div className="mt-2 space-y-1">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            data-testid="add-patient-email-otp-input"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={emailOtpCode}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                setEmailOtpCode(val);
                                                setEmailOtpError('');
                                                if (val.length === 6) handleVerifyEmailOtp(val);
                                            }}
                                            placeholder={t('addPatient.enter_otp')}
                                            className="flex-1 min-w-0 border border-teal-300 rounded-lg px-3 py-1.5 text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-400"
                                        />
                                        <button
                                            type="button"
                                            data-testid="add-patient-email-otp-verify-button"
                                            onClick={() => handleVerifyEmailOtp()}
                                            disabled={verifyOtpLoading || emailOtpCode.length !== 6}
                                            className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                        >
                                            {verifyOtpLoading ? '...' : t('addPatient.verify')}
                                        </button>
                                    </div>
                                    <div>
                                        {emailOtpTimer > 0 ? (
                                            <span className="text-xs text-gray-500">{t('addPatient.resend_in', { seconds: emailOtpTimer })}</span>
                                        ) : (
                                            <button type="button" data-testid="add-patient-email-otp-resend-button" onClick={handleSendEmailOtp} disabled={sendOtpLoading} className="text-xs text-teal-600 font-semibold hover:underline disabled:opacity-50">
                                                {t('addPatient.resend_otp')}
                                            </button>
                                        )}
                                    </div>
                                    {emailOtpError && <p className="text-red-500 text-xs">{emailOtpError}</p>}
                                </div>
                            )}
                            {emailOtpVerified && (
                                <p className="text-green-600 text-xs mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t('addPatient.email_otp_verified')}</p>
                            )}
                        </div>
                        <div data-testid="phone-field-container">
                            <label htmlFor="phone" data-testid="phone-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.phone')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 ${!formData.countryId ? 'bg-gray-100' : 'bg-white'} ${phoneOtpVerified
                                ? 'border-green-400 focus-within:ring-2 focus-within:ring-green-500'
                                : phoneStatus === false
                                    ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                                    : phoneStatus === true
                                        ? 'border-teal-300 focus-within:ring-2 focus-within:ring-teal-500'
                                        : errors.phone
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Phone className={`w-5 h-5 mr-2 flex-shrink-0 ${!formData.countryId ? 'text-gray-400' : 'text-[#4ABDBD]'}`} />
                                <PhoneCountryDropdown
                                    countryTimezoneList={countryTimezoneList}
                                    countryId={formData.countryId}
                                    onSelect={({ countryId, timeZone }) => {
                                        setFormData(prev => ({ ...prev, countryId, timeZone, phone: '', altPhone: '', zipCode: '' }));
                                        setErrors(prev => ({ ...prev, countryId: null, timeZone: null }));
                                        setPhoneStatus(null);
                                        if (lastCheckedPhoneRef) lastCheckedPhoneRef.current = '';
                                        if (resetOtpPhoneState) resetOtpPhoneState();
                                    }}
                                    disabled={phoneOtpVerified}
                                />
                                <input
                                    data-testid="phone-input"
                                    id="input-phone"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                                    maxLength={selectedCountry?.mobileMaxLength || 15}
                                    disabled={!formData.countryId || phoneOtpVerified}
                                    placeholder={!formData.countryId ? t('addPatient.select_country_first', 'Select country first') : t('addPatient.phone_placeholder', { length: selectedCountry?.mobileMinLength === selectedCountry?.mobileMaxLength ? selectedCountry?.mobileMinLength : `${selectedCountry?.mobileMinLength}-${selectedCountry?.mobileMaxLength}` })}
                                    className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm ${!formData.countryId ? 'cursor-not-allowed' : ''}`}
                                />
                                {mobileCheckLoading && (
                                    <span className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2 flex-shrink-0"></span>
                                )}
                                {!mobileCheckLoading && phoneStatus === false && (
                                    <X
                                        className="text-red-500 w-5 h-5 ml-2 flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, phone: '' }));
                                            setPhoneStatus(null);
                                            setErrors(prev => ({ ...prev, phone: null }));
                                            lastCheckedPhoneRef.current = '';
                                            resetOtpPhoneState();
                                        }}
                                    />
                                )}
                                {!mobileCheckLoading && phoneStatus === true && !phoneOtpVerified && (
                                    <button
                                        type="button"
                                        data-testid="add-patient-phone-otp-send-button"
                                        onClick={handleSendPhoneOtp}
                                        disabled={sendOtpLoading || phoneOtpSent}
                                        className="ml-2 flex-shrink-0 text-xs px-2 py-1 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                    >
                                        {sendOtpLoading ? '...' : phoneOtpSent ? t('addPatient.sent') : t('addPatient.send_otp')}
                                    </button>
                                )}
                                {phoneOtpVerified && (
                                    <ShieldCheck className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                                )}
                            </div>
                            {errors.phone && <p data-testid="phone-error" className="mt-2 text-xs text-red-500 font-medium">{errors.phone}</p>}
                            {!mobileCheckLoading && phoneStatus === false && (
                                <p className="text-red-500 text-xs mt-1">{t('addPatient.phone_already_exists')}</p>
                            )}
                            {/* Phone OTP input row */}
                            {phoneOtpSent && !phoneOtpVerified && (
                                <div className="mt-2 space-y-1">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            data-testid="add-patient-phone-otp-input"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={phoneOtpCode}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                setPhoneOtpCode(val);
                                                setPhoneOtpError('');
                                                if (val.length === 6) handleVerifyPhoneOtp(val);
                                            }}
                                            placeholder={t('addPatient.enter_otp')}
                                            className="flex-1 min-w-0 border border-teal-300 rounded-lg px-3 py-1.5 text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-400"
                                        />
                                        <button
                                            type="button"
                                            data-testid="add-patient-phone-otp-verify-button"
                                            onClick={() => handleVerifyPhoneOtp()}
                                            disabled={verifyOtpLoading || phoneOtpCode.length !== 6}
                                            className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                        >
                                            {verifyOtpLoading ? '...' : t('addPatient.verify')}
                                        </button>
                                    </div>
                                    <div>
                                        {phoneOtpTimer > 0 ? (
                                            <span className="text-xs text-gray-500">{t('addPatient.resend_in', { seconds: phoneOtpTimer })}</span>
                                        ) : (
                                            <button type="button" data-testid="add-patient-phone-otp-resend-button" onClick={handleSendPhoneOtp} disabled={sendOtpLoading} className="text-xs text-teal-600 font-semibold hover:underline disabled:opacity-50">
                                                {t('addPatient.resend_otp')}
                                            </button>
                                        )}
                                    </div>
                                    {phoneOtpError && <p className="text-red-500 text-xs">{phoneOtpError}</p>}
                                </div>
                            )}
                            {phoneOtpVerified && (
                                <p className="text-green-600 text-xs mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t('addPatient.mobile_otp_verified')}</p>
                            )}
                        </div>
                        <div data-testid="alt-phone-field-container">
                            <label htmlFor="altPhone" data-testid="alt-phone-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.alt_phone', 'Alternate mobile no')}</label>
                            <div className={`relative flex items-center border rounded-xl p-3 ${!formData.countryId ? 'bg-gray-100' : 'bg-white'
                                } ${errors.altPhone ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Phone className={`w-5 h-5 mr-2 ${!formData.countryId ? 'text-gray-400' : 'text-[#4ABDBD]'}`} />
                                <input
                                    data-testid="alt-phone-input"
                                    id="input-altPhone"
                                    name="altPhone"
                                    type="tel"
                                    value={formData.altPhone}
                                    onChange={handleChange}
                                    onKeyPress={(e) => {
                                        if (!/[0-9]/.test(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    maxLength={selectedCountry?.altMaxLength || selectedCountry?.mobileMaxLength || 15}
                                    disabled={!formData.countryId}
                                    placeholder={!formData.countryId ? t('addPatient.select_country_first', 'Select country first') : t('addPatient.alt_phone_placeholder', { length: (selectedCountry?.altMinLength || selectedCountry?.mobileMinLength) === (selectedCountry?.altMaxLength || selectedCountry?.mobileMaxLength) ? (selectedCountry?.altMinLength || selectedCountry?.mobileMinLength) : `${selectedCountry?.altMinLength || selectedCountry?.mobileMinLength}-${selectedCountry?.altMaxLength || selectedCountry?.mobileMaxLength}` })}
                                    className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm ${!formData.countryId ? 'cursor-not-allowed' : ''}`}
                                />
                            </div>
                            {errors.altPhone && <p data-testid="alt-phone-error" className="mt-2 text-xs text-red-500 font-medium">{errors.altPhone}</p>}
                        </div>
                        <div data-testid="address-field-container">
                            <label htmlFor="address" data-testid="address-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.address')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.address ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Home className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="address-input"
                                    id="input-address"
                                    name="address"
                                    type="text"
                                    value={formData.address}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.address_placeholder')}
                                    maxLength={100}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.address && <p data-testid="address-error" className="mt-2 text-xs text-red-500 font-medium">{errors.address}</p>}
                        </div>
                        <div data-testid="additional-field-container">
                            <label htmlFor="additional" data-testid="additional-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.additional')}</label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.additional ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <FileText className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="additional-input"
                                    id="input-additional"
                                    name="additional"
                                    type="text"
                                    value={formData.additional}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.additional_placeholder')}
                                    maxLength={150}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.additional && <p data-testid="additional-error" className="mt-2 text-xs text-red-500 font-medium">{errors.additional}</p>}
                        </div>
                        {/* Postal code */}
                        <div data-testid="zipcode-field-container">
                            <label htmlFor="zipCode" data-testid="zipcode-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.zipcode')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 ${!formData.countryId ? 'bg-gray-100' : 'bg-white'
                                } ${errors.zipCode ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <MapPin className={`w-5 h-5 mr-2 ${!formData.countryId ? 'text-gray-400' : 'text-[#4ABDBD]'}`} />
                                <input
                                    data-testid="zipcode-input"
                                    id="input-zipCode"
                                    name="zipCode"
                                    type="text"
                                    value={formData.zipCode}
                                    onChange={handleChange}
                                    onKeyPress={(e) => {
                                        if (!/[0-9]/.test(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    maxLength={selectedCountry?.zipMaxLength || 10}
                                    disabled={!formData.countryId}
                                    placeholder={!formData.countryId ? t('addPatient.select_country_first', 'Select country first') : t('addPatient.zipcode_placeholder', { length: selectedCountry?.zipMinLength === selectedCountry?.zipMaxLength ? selectedCountry?.zipMinLength : `${selectedCountry?.zipMinLength}-${selectedCountry?.zipMaxLength}` })}
                                    className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm ${!formData.countryId ? 'cursor-not-allowed' : ''}`}
                                />
                            </div>
                            {errors.zipCode && <p data-testid="zipcode-error" className="mt-2 text-xs text-red-500 font-medium">{errors.zipCode}</p>}
                        </div>
                        {/* City */}
                        <div data-testid="city-field-container">
                            <label htmlFor="city" data-testid="city-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.city')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.city ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <MapPin className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="city-input"
                                    id="input-city"
                                    name="city"
                                    type="text"
                                    value={formData.city}
                                    onChange={handleChange}
                                    placeholder={t('addPatient.city_placeholder')}
                                    maxLength={25}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.city && <p data-testid="city-error" className="mt-2 text-xs text-red-500 font-medium">{errors.city}</p>}
                        </div>
                        {/* Country (auto-syncs with phone country code via shared formData.countryId) */}
                        <div data-testid="country-field-container">
                            <label htmlFor="country" data-testid="country-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addPatient.country')} <span className="text-red-500">*</span></label>
                            <CountryTimeZoneSelect
                                countryTimezoneList={countryTimezoneList}
                                countryId={formData.countryId}
                                timeZone={formData.timeZone}
                                onChange={({ countryId, timeZone }) => {
                                    setFormData(prev => ({ ...prev, countryId, timeZone, phone: '', altPhone: '', zipCode: '' }));
                                    setErrors(prev => ({ ...prev, countryId: null, timeZone: null }));
                                    setPhoneStatus(null);
                                    if (lastCheckedPhoneRef) lastCheckedPhoneRef.current = '';
                                    if (resetOtpPhoneState) resetOtpPhoneState();
                                }}
                                placeholder={t('addPatient.select_country')}
                                error={!!errors.countryId || !!errors.timeZone}
                            />
                            {errors.countryId && <p data-testid="country-error" className="mt-2 text-xs text-red-500 font-medium">{errors.countryId}</p>}
                            {errors.timeZone && <p className="mt-2 text-xs text-red-500 font-medium">{errors.timeZone}</p>}
                        </div>
                    </form>
                </div>

                {/* Footer Section - Sticky */}
                <div data-testid="form-actions-container" className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                    <button
                        data-testid="cancel-button"
                        id="btn-cancel"
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                        disabled={patientLoading}
                    >
                        {t('addPatient.cancel')}
                    </button>
                    <button
                        data-testid="submit-button"
                        id="btn-submit"
                        type="submit"
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2"
                        disabled={patientLoading}
                    >
                        {patientLoading && (
                            <span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                        )}
                        {patientLoading ? t('addPatient.creating') : t('addPatient.create')}
                    </button>
                </div>
            </div>
        </div>
    );
}